/*
 * ============================================================
 * White Sands Holdings — Navigation controller
 * ------------------------------------------------------------
 * Lightweight, dependency-free. Handles:
 *   - Mobile drawer open/close (button, backdrop, Escape)
 *   - Focus trap while the drawer is open
 *   - Body scroll lock
 *   - Returning focus to the trigger on close
 *   - aria-expanded sync for all <details> disclosures
 *   - Escape closes open desktop dropdowns
 * Re-initialises on Shopify Theme Editor section loads.
 * ============================================================
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function initDrawer() {
    var drawer = document.querySelector('[data-wsh-drawer]');
    var backdrop = document.querySelector('[data-wsh-backdrop]');
    var openBtn = document.querySelector('[data-wsh-open]');
    var closeBtn = document.querySelector('[data-wsh-close]');

    if (!drawer || !openBtn) return;

    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.setAttribute('data-open', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      if (backdrop) {
        backdrop.hidden = false;
        backdrop.setAttribute('data-open', 'true');
      }
      openBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('wsh-scroll-lock');
      document.body.classList.add('wsh-scroll-lock');
      // Move focus into the drawer (the close button).
      if (closeBtn) closeBtn.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      drawer.setAttribute('data-open', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      if (backdrop) {
        backdrop.setAttribute('data-open', 'false');
        // Hide after the transition so it is not tab-reachable.
        window.setTimeout(function () {
          if (backdrop.getAttribute('data-open') !== 'true') backdrop.hidden = true;
        }, 400);
      }
      openBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('wsh-scroll-lock');
      document.body.classList.remove('wsh-scroll-lock');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      // Focus trap.
      var nodes = drawer.querySelectorAll(FOCUSABLE);
      var focusable = Array.prototype.filter.call(nodes, function (el) {
        return el.offsetParent !== null; // visible only
      });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);
  }

  /* Keep aria-expanded on every disclosure summary in sync with
     the native <details> open state (desktop dropdowns + drawer). */
  function initDisclosures() {
    var summaries = document.querySelectorAll('.wsh-nav__summary, .wsh-drawer__summary');
    Array.prototype.forEach.call(summaries, function (summary) {
      var details = summary.closest('details');
      if (!details || details.dataset.wshBound) return;
      details.dataset.wshBound = 'true';
      details.addEventListener('toggle', function () {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      });
    });
  }

  /* Escape closes any open desktop dropdown and returns focus. */
  function initDropdownEscape() {
    var header = document.querySelector('[data-wsh-header]');
    if (!header || header.dataset.wshEscBound) return;
    header.dataset.wshEscBound = 'true';
    header.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = header.querySelector('details[open]');
      if (!open) return;
      open.removeAttribute('open');
      var summary = open.querySelector('summary');
      if (summary) summary.focus();
    });
  }

  function initAll() {
    initDrawer();
    initDisclosures();
    initDropdownEscape();
  }

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', initAll);
  }

  // Theme Editor: re-bind when the header section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-wsh-header]')) {
      initAll();
    } else {
      initAll();
    }
  });
})();
